import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, Cloud, DatabaseBackup, HardDrive, Loader2,
  RefreshCw, Save, TestTube2, Upload,
} from 'lucide-react';
import client from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import ConfirmDialog from './ConfirmDialog';
import {
  exampleRemoteKey, formatBackupDate, formatBackupSize, remoteChanges,
  remoteFormFromConfig,
} from '../lib/backupRemote';

const errorText = (error, fallback) => (
  error?.response?.data?.detail || fallback
);

const Field = ({ label, hint, children }) => (
  <div>
    <label className="settings-field-label">{label}</label>
    {children}
    {hint ? <div className="mt-1 text-xs text-slate-500">{hint}</div> : null}
  </div>
);

/**
 * Cloudflare R2 bağlantı ayarları (Ayarlar → Yedekleme).
 *
 * Ayarlar `settings` tablosunda değil `provider_configs`'in `backup_r2`
 * satırındadır ve kendi ucundan yazılır (`PATCH /admin/backup/remote/config`);
 * ana Kaydet şeridine bağlanmaz — gizli anahtar o gövdeye hiç girmemelidir.
 */
export const BackupRemoteSettings = ({ remote, retentionDays = 3, onSaved }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(() => remoteFormFromConfig(remote));
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Durum ucu iş sürerken birkaç saniyede bir yoklanır; form yalnızca
  // **kayıtlı değer** değiştiğinde tazelenir, yoksa yazılan alan silinirdi.
  const savedSignature = JSON.stringify(remoteFormFromConfig(remote));
  useEffect(() => {
    setForm(remoteFormFromConfig(remote));
  }, [savedSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  const changes = remoteChanges(form, remote);
  const dirty = Object.keys(changes).length > 0;
  const set = (key) => (event) => {
    const value = event.target.type === 'checkbox'
      ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
    setTestResult(null);
  };

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const { data } = await client.patch(
        '/admin/backup/remote/config', changes);
      toast({
        title: 'Uzak yedek ayarları kaydedildi',
        description: data.ready
          ? "Sonraki yedek R2'ye de gönderilecek."
          : 'Uzak yedekleme kapalı; yedekler yalnızca sunucuda kalır.',
      });
      setForm(remoteFormFromConfig(data));
      onSaved?.(data);
    } catch (error) {
      toast({
        title: 'Uzak yedek ayarları kaydedilemedi',
        description: errorText(error, 'İşlem başarısız.'),
      });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await client.post('/admin/backup/remote/test');
      setTestResult(data);
    } catch (error) {
      setTestResult({ ok: false, error: errorText(error, 'Test yapılamadı.') });
    } finally {
      setTesting(false);
    }
  };

  const statusBadge = remote?.ready
    ? { text: 'Açık · her yedek R2\'ye gider', cls: 'badge-green' }
    : remote?.enabled
      ? { text: 'Bilgiler eksik', cls: 'badge-yellow' }
      : { text: 'Kapalı', cls: 'badge-yellow' };

  return (
    <div className="admin-fieldset mt-4" data-testid="backup-remote-settings">
      <div className="admin-fieldset-title flex items-center gap-2">
        <Cloud size={14} className="text-cyan-400" />
        Cloudflare R2 — uzak yedek
        <span className={`badge-pill ${statusBadge.cls} ml-auto`}>{statusBadge.text}</span>
      </div>
      <div className="-mt-2 mb-3 text-xs leading-5 text-slate-500">
        Sunucuda duran kopya bir arıza yedeğidir — sunucuyu kaybederseniz o da gider.
        R2 bağlandığında her yedek (veritabanı + bütün görseller ve belgeler) üretildiği
        anda kovaya da yazılır ve orada da <b className="text-slate-300">
        {retentionDays} gün</b> tutulur. Anahtarı Cloudflare panelinde
        <b className="text-slate-300"> R2 → Manage R2 API Tokens</b> altından,
        yalnızca bu kovaya <i>Object Read &amp; Write</i> yetkisiyle üretin.
      </div>

      <div className="settings-toggles">
        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={Boolean(form.enabled)}
            onChange={set('enabled')}
            data-testid="remote-enabled"
          />
          <span>
            <span className="block">Yedekleri R2&apos;ye de gönder</span>
            <span className="mt-0.5 block text-xs text-slate-500">
              Gönderilemeyen yedek başarısız sayılmaz, sunucuda kalır; geçmişte ayrı
              bir rozetle görünür.
            </span>
          </span>
        </label>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Hesap kimliği (Account ID)" hint="Cloudflare panelinin sağ sütunundaki 32 haneli kimlik.">
          <input className="admin-input" value={form.account_id} onChange={set('account_id')} placeholder="a1b2c3…" autoComplete="off" />
        </Field>
        <Field label="Kova adı (Bucket)">
          <input className="admin-input" value={form.bucket} onChange={set('bucket')} placeholder="site-yedekleri" autoComplete="off" />
        </Field>
        <Field label="Erişim anahtarı (Access Key ID)">
          <input className="admin-input" value={form.access_key_id} onChange={set('access_key_id')} autoComplete="off" />
        </Field>
        <Field
          label="Gizli anahtar (Secret Access Key)"
          hint={remote?.secret_configured
            ? 'Kayıtlı. Değiştirmek için yenisini yazın; boş bırakılırsa kayıtlı anahtar korunur.'
            : 'Güvenlik gereği kaydedildikten sonra panelde gösterilmez.'}
        >
          <input
            type="password"
            className="admin-input"
            value={form.secret_access_key}
            onChange={set('secret_access_key')}
            placeholder={remote?.secret_configured ? '•••••••• kayıtlı' : ''}
            autoComplete="new-password"
            data-testid="remote-secret"
          />
        </Field>
        <Field
          label="Klasör (önek)"
          hint="Aynı kovayı birden çok site kullanıyorsa her site kendi adını yazsın."
        >
          <input className="admin-input" value={form.prefix} onChange={set('prefix')} placeholder="yedekler" autoComplete="off" />
        </Field>
        <Field
          label="Uç adresi (isteğe bağlı)"
          hint="Boş bırakılırsa hesap kimliğinden R2 adresi kurulur. Başka bir S3 uyumlu depo için doldurun."
        >
          <input className="admin-input" value={form.endpoint} onChange={set('endpoint')} placeholder="https://…" autoComplete="off" />
        </Field>
      </div>

      <div className="mt-3 text-xs text-slate-500">
        Yedekler şu düzende yazılır:{' '}
        <code className="text-cyan-400">{exampleRemoteKey(form.prefix)}</code>
        {' '}— her gün kendi klasöründe.
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="admin-btn-primary flex items-center gap-2"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          R2 Ayarlarını Kaydet
        </button>
        <button
          type="button"
          onClick={test}
          disabled={testing || dirty || !remote?.configured}
          title={dirty ? 'Önce değişiklikleri kaydedin' : undefined}
          className="admin-btn-secondary flex items-center gap-2"
        >
          {testing ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
          Bağlantıyı Test Et
        </button>
      </div>

      {testResult && (
        <div
          className={`settings-note mt-3 mb-0 ${testResult.ok ? 'is-info' : 'is-danger'}`}
          data-testid="remote-test-result"
        >
          {testResult.ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          <div>
            {testResult.ok
              ? <>Bağlantı kuruldu: <b>{testResult.bucket}</b> kovası okunabiliyor.</>
              : <>Bağlantı kurulamadı: {testResult.error}</>}
          </div>
        </div>
      )}
    </div>
  );
};

const SOURCE_LABEL = { remote: "Cloudflare R2", local: 'sunucu' };

const BackupRow = ({ file, source, disabled, onPick }) => (
  <div className="flex items-center gap-3 text-sm p-2 rounded-lg" style={{ background: 'var(--v2-surface-2, rgba(15,23,42,0.5))' }}>
    {source === 'remote'
      ? <Cloud size={14} className="text-cyan-400" />
      : <HardDrive size={14} className="text-cyan-400" />}
    <div className="flex-1 min-w-0">
      <div className="truncate">{file.filename}</div>
      <div className="text-xs text-slate-500">
        {formatBackupSize(file.size_bytes)} • {formatBackupDate(file.created_at)}
      </div>
    </div>
    <button
      type="button"
      onClick={() => onPick(file, source)}
      disabled={disabled}
      className="admin-btn-secondary flex items-center gap-1 whitespace-nowrap"
    >
      <Upload size={14} /> Aktar
    </button>
  </div>
);

// "Bu yedek aktarılsın mı?" kartı: kaynakta bir yedek varsa en yenisini öne
// çıkarır ve soruyu doğrudan sorar.
const LatestCard = ({ title, icon, file, source, disabled, onPick, empty }) => (
  <div className="settings-stat" data-testid={`latest-${source}`}>
    <div className="settings-stat-label">{icon} {title}</div>
    {file ? (
      <div className="mt-1 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0 text-sm">
          <b className="text-slate-100">{formatBackupDate(file.created_at)}</b>
          {' '}tarihli yedek bulundu ({formatBackupSize(file.size_bytes)}).
          <span className="text-slate-400"> Bu yedek sisteme aktarılsın mı?</span>
        </div>
        <button
          type="button"
          onClick={() => onPick(file, source)}
          disabled={disabled}
          className="admin-btn-primary flex items-center gap-2 whitespace-nowrap"
        >
          <Upload size={14} /> Aktar
        </button>
      </div>
    ) : (
      <div className="mt-1 text-sm text-slate-500">{empty}</div>
    )}
  </div>
);

/**
 * Hazır bir yedekten geri dönüş: R2'deki ve sunucudaki yedekler.
 *
 * Kovadaki liste **sekme açılışında bir kez** istenir (`/remote/files`):
 * durum ucu iş sürerken saniyede bir yoklanır ve her yoklamada Cloudflare'a
 * çıkmak isteği üçüncü tarafın gecikmesine bağlardı.
 */
export const BackupRestoreSources = ({
  status, disabled, onStarted, confirmPhrase = '',
}) => {
  const { toast } = useToast();
  const remote = status?.remote;
  const remoteReady = Boolean(remote?.ready);
  const [remoteList, setRemoteList] = useState({
    loading: false, loaded: false, files: [], latest: null, error: '',
  });
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadRemote = useCallback(async () => {
    setRemoteList((current) => ({ ...current, loading: true, error: '' }));
    try {
      const { data } = await client.get('/admin/backup/remote/files');
      setRemoteList({
        loading: false,
        loaded: true,
        files: data.files || [],
        latest: data.latest || null,
        error: data.error || '',
      });
    } catch (error) {
      setRemoteList({
        loading: false,
        loaded: true,
        files: [],
        latest: null,
        error: errorText(error, "R2'deki yedekler okunamadı."),
      });
    }
  }, []);

  useEffect(() => {
    if (remoteReady) loadRemote();
  }, [remoteReady, loadRemote]);

  const localFiles = (status?.files || []).filter((file) => file.format === 'zip');
  const localLatest = localFiles[0] || (status?.files || [])[0] || null;
  const otherLocal = (status?.files || []).filter(
    (file) => file.filename !== localLatest?.filename);
  const otherRemote = remoteList.files.filter(
    (file) => file.key !== remoteList.latest?.key);
  const blocked = disabled || busy;

  const pick = (file, source) => setPending({ file, source });

  const start = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await client.post('/admin/backup/restore-existing', null, {
        params: {
          source: pending.source,
          name: pending.source === 'remote' ? pending.file.key : pending.file.filename,
        },
      });
      toast({
        title: 'Aktarım başladı',
        description: pending.source === 'remote'
          ? "Yedek R2'den indiriliyor; ardından güvenlik kopyası alınıp uygulanacak."
          : 'Önce güvenlik kopyası alınır, sonra yedek uygulanır.',
      });
      setPending(null);
      onStarted?.();
    } catch (error) {
      toast({
        title: 'Aktarım başlatılamadı',
        description: errorText(error, 'İşlem başarısız.'),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="backup-restore-sources">
      <LatestCard
        title="Cloudflare R2'deki son yedek"
        icon={<Cloud size={12} />}
        source="remote"
        file={remoteReady ? remoteList.latest : null}
        disabled={blocked}
        onPick={pick}
        empty={!remoteReady
          ? "R2 bağlı değil. Yukarıdaki bölümden bağlandığında kovadaki yedekler burada listelenir."
          : remoteList.loading
            ? 'Kovadaki yedekler kontrol ediliyor…'
            : remoteList.error
              ? <span className="text-red-400">{remoteList.error}</span>
              : "R2'de bu kuruluma ait yedek bulunamadı."}
      />
      {remoteReady && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={loadRemote}
            disabled={remoteList.loading}
            className="admin-btn-secondary flex items-center gap-1 text-xs"
          >
            {remoteList.loading
              ? <Loader2 size={12} className="animate-spin" />
              : <RefreshCw size={12} />} R2&apos;yi yeniden kontrol et
          </button>
        </div>
      )}
      {!!otherRemote.length && (
        <div className="space-y-2">
          {otherRemote.map((file) => (
            <BackupRow key={file.key} file={file} source="remote" disabled={blocked} onPick={pick} />
          ))}
        </div>
      )}

      <LatestCard
        title="Sunucudaki son yedek"
        icon={<DatabaseBackup size={12} />}
        source="local"
        file={localLatest}
        disabled={blocked}
        onPick={pick}
        empty="Sunucuda yedek dosyası yok."
      />
      {!!otherLocal.length && (
        <div className="space-y-2">
          {otherLocal.map((file) => (
            <BackupRow key={file.filename} file={file} source="local" disabled={blocked} onPick={pick} />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pending)}
        title="Yedek aktarılsın mı?"
        description={pending
          ? `"${pending.file.filename}" (${SOURCE_LABEL[pending.source]}) sisteme aktarılacak: `
            + 'veritabanının tamamı ve yükleme klasörü (görseller, dekontlar, KYC '
            + 'belgeleri) yedektekiyle değiştirilir. İşlemden önce otomatik bir güvenlik '
            + 'kopyası alınır. Aktarım bitince yedekteki yönetici şifresiyle yeniden giriş '
            + 'yapmanız gerekebilir.'
          : ''}
        confirmLabel="Aktar"
        tone="danger"
        confirmPhrase={confirmPhrase}
        busy={busy}
        onCancel={() => { if (!busy) setPending(null); }}
        onConfirm={start}
      />
    </div>
  );
};
